// Streaming zip (§8) via fflate (MIT). Already-compressed formats (JPEG, PNG) are stored with
// ZipPassThrough; TIFF and the contact-sheet PDF go through ZipDeflate. Chunks are collected and handed
// to the caller as one Blob (no second full-size copy in memory: CODE_REVIEW S17), saved with fs.saveAs.
// fflate writes no ZIP64, so an archive must stay under 4 GB: past ZIP_MAX each further file is refused
// with a message saying to export to a folder instead.
import { Zip, ZipDeflate, ZipPassThrough } from 'fflate';

const STORED = /\.(jpe?g|png)$/i;

/** Largest archive we build (fflate has no ZIP64; 4 GiB minus headroom for headers). */
export const ZIP_MAX = 3.5e9;
export class ZipTooLargeError extends Error {
  override name = 'ZipTooLargeError';
}

export class ZipBuilder {
  private zip: Zip;
  private chunks: Uint8Array<ArrayBuffer>[] = [];
  private total = 0;
  private done: Promise<Blob>;
  private resolve!: (b: Blob) => void;
  private reject!: (e: Error) => void;
  readonly names: string[] = [];

  constructor(private readonly max = ZIP_MAX) {
    this.done = new Promise((res, rej) => {
      this.resolve = res;
      this.reject = rej;
    });
    this.zip = new Zip((err, data, final) => {
      if (err) return this.reject(new Error(err.message));
      this.chunks.push(data as Uint8Array<ArrayBuffer>);
      if (final) this.resolve(new Blob(this.chunks, { type: 'application/zip' }));
    });
  }

  add(path: string, bytes: Uint8Array) {
    if (this.total + bytes.length > this.max)
      throw new ZipTooLargeError('The zip would be larger than 3.5 GB. Export to a folder instead.');
    this.total += bytes.length;
    const f = STORED.test(path) ? new ZipPassThrough(path) : new ZipDeflate(path, { level: 6 });
    this.zip.add(f);
    f.push(bytes, true);
    this.names.push(path);
  }

  finish(): Promise<Blob> {
    this.zip.end();
    return this.done;
  }
}
